import React, { useState } from "react";
import { api, extractError } from "../lib/api";
import { SectionCard, Pill } from "./Ui";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

export default function AdminContractPurge() {
  const [contractId, setContractId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadPreview = async () => {
    const id = contractId.trim();
    if (!id) {
      toast.error("Enter a contract ID");
      return;
    }
    setLoading(true);
    setPreview(null);
    try {
      const res = await api.get(`/purchase-contracts/${id}/purge-preview`);
      setPreview(res.data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  const runPurge = async () => {
    const id = contractId.trim();
    if (!id || !preview) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/purchase-contracts/${id}/purge`, {
        force: Boolean(preview.requires_force),
      });
      toast.success(
        res.data?.purchases_fully_removed
          ? `Contract and ${res.data.purchases_fully_removed} linked purchase(s) permanently deleted`
          : "Contract permanently deleted",
      );
      setConfirmOpen(false);
      setPreview(null);
      setContractId("");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Permanently delete purchase contract">
        <p className="text-sm text-muted-foreground mb-4">
          Administrator only. Removes the contract and all linked truck purchases, vouchers, lab records,
          unloading data, and stack sample links. This cannot be undone. Works on active or archived contracts.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end max-w-xl">
          <div className="flex-1">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Contract ID</Label>
            <Input
              className="mt-1.5 font-mono"
              placeholder="e.g. 42"
              value={contractId}
              onChange={(e) => {
                setContractId(e.target.value);
                setPreview(null);
              }}
              data-testid="admin-contract-purge-id"
            />
          </div>
          <Button type="button" variant="outline" onClick={loadPreview} disabled={loading || !contractId.trim()}>
            {loading ? "Loading…" : "Preview impact"}
          </Button>
        </div>

        {preview && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/80 p-4 text-sm space-y-2">
            <div className="flex items-center gap-2 font-medium text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {preview.contract_number}
              {preview.has_open_purchase && (
                <Pill tone="gold">Trucks in progress</Pill>
              )}
            </div>
            <ul className="list-disc pl-5 text-amber-950/90 space-y-1">
              {preview.purchases_fully_removed > 0 && (
                <li>
                  {preview.purchases_fully_removed} truck purchase(s) will be removed
                  {preview.purchase_codes_fully_removed?.length > 0 && (
                    <> ({preview.purchase_codes_fully_removed.join(", ")})</>
                  )}
                </li>
              )}
              {preview.purchases_partially_updated > 0 && (
                <li>
                  Unlink from {preview.purchases_partially_updated} multi-contract truck(s)
                </li>
              )}
              {preview.vouchers > 0 && <li>{preview.vouchers} voucher(s)</li>}
              {preview.stack_sample_intakes_removed > 0 && (
                <li>{preview.stack_sample_intakes_removed} stack sample intake(s)</li>
              )}
              {!preview.purchases_fully_removed &&
                !preview.purchases_partially_updated &&
                !preview.vouchers &&
                !preview.stack_sample_intakes_removed && (
                  <li>Only the contract row will be removed.</li>
                )}
            </ul>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="mt-2"
              onClick={() => setConfirmOpen(true)}
              data-testid="admin-contract-purge-submit"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete permanently
            </Button>
          </div>
        )}
      </SectionCard>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete contract?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove contract <strong>{preview?.contract_number}</strong> and all linked
              records. This action cannot be undone.
              {preview?.requires_force && (
                <span className="block mt-2 text-destructive font-medium">
                  Trucks are still in progress — they will be deleted too.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runPurge}
              disabled={submitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {submitting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
